import React from 'react';
import * as Scrivito from 'scrivito';

Scrivito.provideWidgetClass('TestimonialWidget', {
  attributes: {
    image: 'reference',
    alt: 'string',
    content: 'string',
    name: 'string',
    jobTitle: 'string',
    color: ['enum', { values: ['very_light', 'light', 'dark'] }],
    pdf: 'reference',
    pdfLinkTitle: 'string',
  },
});

// Map CMS enum values to the card's accent modifier class (mirrors Rails
// TestimonialWidget#color, which used to pick the whole card's background —
// see main.scss's .tt-testimonial--* rules for what it accents now).
const COLOR_CLASS = {
  very_light: 'tt-testimonial--mist',
  light: 'tt-testimonial--steel',
  dark: 'tt-testimonial--ink',
};

const TestimonialWidget = Scrivito.connect(({ widget }) => {
  const colorEnum = widget.get('color');
  const color = COLOR_CLASS[colorEnum] || 'tt-testimonial--mist';
  const pdf = widget.get('pdf');
  // Rails linked to `scrivito_path(widget.pdf)`, which its Scrivito routes served
  // as the file itself. This app has no route for a binary obj, so the link goes
  // straight to the CDN copy.
  const pdfUrl = pdf ? pdf.contentUrl() : null;
  const image = widget.get('image');
  const avatar = image && image.objClass() === 'Image' ? image.get('blob') : null;

  return (
    <div className={`tt-testimonial ${color}`}>
      <div className="quote">
        {/* With a PDF attached, the Rails template wrapped the quote in
            .pdf-present – which is what trims 20px off the padding below the
            text – and put the download link underneath as a block element. */}
        {pdfUrl ? (
          <>
            <blockquote>
              <div className="pdf-present">
                <Scrivito.ContentTag tag="p" attribute="content" content={widget} />
              </div>
            </blockquote>
            <a href={pdfUrl}>
              {/* The Rails markup also gave this div the class `ti-save`, but the
                  glyph never showed – the icon font 404'd – so what the old site
                  displayed is the floppy-disk character below. Self-hosting the
                  font would now draw the Themify save icon in front of it, i.e.
                  the same symbol twice, so the class is left off. */}
              <div className="testimonial-icon">&#x1F4BE;</div>
            </a>
          </>
        ) : (
          <blockquote>
            <Scrivito.ContentTag tag="p" attribute="content" content={widget} />
          </blockquote>
        )}
      </div>
      <div className="quote-footer">
        <div className="quote-author-img">
          {/* Rendered at a fixed 48x48 (main.scss) regardless of the source
              photo's aspect ratio; object-fit: cover on the img itself
              (main.scss) avoids stretching it to fit the square (Lighthouse:
              "image-aspect-ratio").

              Explicitly transformed rather than handing ImageTag the
              widget/attribute pair: for an untransformed binary ImageTag
              swaps in a copy sized to the *screen* width x devicePixelRatio
              after hydration (scaleDownBinary in the SDK), i.e. a ~1000px
              photo for a 48px avatar - Lighthouse flagged 30KB+ per
              avatar. An explicitly transformed binary is rendered as is.
              144px bounds leave 2x density for the cropped square at any
              reasonable portrait/landscape ratio. The image stays editable
              via the widget's properties (see provideEditingConfig below). */}
          <Scrivito.ImageTag
            content={avatar ? avatar.optimizeFor({ width: 144, height: 144 }) : null}
            alt={widget.get('alt') || ''}
            width={48}
            height={48}
            loading="lazy"
          />
        </div>
        <Scrivito.ContentTag tag="h3" attribute="name" content={widget} />
        <Scrivito.ContentTag tag="strong" attribute="jobTitle" content={widget} className="company" />
      </div>
    </div>
  );
});

Scrivito.provideComponent('TestimonialWidget', TestimonialWidget);

Scrivito.provideEditingConfig('TestimonialWidget', {
  title: 'Testimonial',
  properties: ['content', 'name', 'jobTitle', 'color', 'image', 'alt', 'pdf', 'pdfLinkTitle'],
  attributes: {
    content:      { title: 'Quote Text' },
    name:         { title: 'Person Name' },
    jobTitle:     { title: 'Job Title / Company' },
    color: {
      title: 'Background Color',
      values: [
        { value: 'very_light', title: 'Blue Superlight' },
        { value: 'light',      title: 'Blue Light' },
        { value: 'dark',       title: 'Blue Dark' },
      ],
    },
    image:        { title: 'Portrait Photo' },
    alt:          { title: 'Image Alt Text' },
    pdf:          { title: 'PDF Attachment' },
    pdfLinkTitle: { title: 'PDF Link Label' },
  },
  initialContent: {
    color: 'very_light',
  },
});

export default TestimonialWidget;
